<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from "vue";

/**
 * Generic floating select / combobox with:
 * - teleport to body (avoids clipping)
 * - search (optional)
 * - keyboard navigation
 * - auto direction (open up/down)
 * - dynamic height constraint
 * Slots:
 *  trigger       -> custom trigger (receives { open, toggle }) else default button
 *  item          -> each option (slot props: { option, index, highlighted, select })
 *  empty         -> when no results
 *  before-list   -> optional content above list (e.g. filters)
 *  after-list    -> optional content below list
 */

const props = defineProps({
  modelValue: { type: [String, Number, Object, Array, null], default: null },
  options: { type: Array, default: () => [] },
  optionKey: { type: String, default: "id" },
  optionLabel: { type: [String, Function], default: "label" },
  searchable: { type: Boolean, default: true },
  placeholder: { type: String, default: "Suchen..." },
  minWidth: { type: Number, default: 280 },
  maxWidth: { type: Number, default: 440 },
  minHeight: { type: Number, default: 120 },
  maxHeight: { type: Number, default: 360 },
  maxVisibleItems: { type: Number, default: 8 },
  itemHeight: { type: Number, default: 44 },
  popoverMargin: { type: Number, default: 8 },
  autoDirection: { type: Boolean, default: true },
  closeOnSelect: { type: Boolean, default: true },
  returnObject: { type: Boolean, default: true },
  showChevron: { type: Boolean, default: true },
  searchProp: { type: [String, Array], default: null },
  groupedSearch: { type: Boolean, default: false },
  teleportZ: { type: Number, default: 2600 },
  triggerClass: {
    type: String,
    default: "btn btn--pill btn--sm btn--outline fs-trigger",
  },
  listClass: { type: String, default: "" },
  filterFn: { type: Function, default: null },
  sortFn: { type: Function, default: (a, b) => 0 },
  disabled: { type: Boolean, default: false },
  ariaLabel: { type: String, default: "Auswahl" },
  autoWidth: { type: Boolean, default: true }, // passe Breite an längste Option (innerhalb min/max)
  allowWrap: { type: Boolean, default: false }, // erlaube Zeilenumbruch statt Ellipsis
  showFullOnHover: { type: Boolean, default: true }, // setzt title-Attribut bei abgeschnittenen Labels
  matchTriggerWidth: { type: Boolean, default: false }, // Popover Breite = Trigger Breite
  size: { type: String, default: "md", validator: (v) => ["sm", "md", "lg"].includes(v) },
});

const emit = defineEmits(["update:modelValue", "select", "open", "close", "search"]);

const open = ref(false);
const query = ref("");
const highlightedIndex = ref(-1);
const triggerRef = ref(null);
const listRef = ref(null);
const inputRef = ref(null);
const popoverStyle = ref({});
const listMaxHeight = ref(null); // dynamic scrollable list height
const measuredWidth = ref(null);
const direction = ref("down"); // 'down' | 'up'
const themeClass = ref(""); // propagate theme when teleported

const normalizedOptions = computed(() => {
  const base = [...props.options];
  if (props.sortFn) base.sort(props.sortFn);
  return base;
});

function getLabel(option) {
  if (typeof props.optionLabel === "function") return props.optionLabel(option);
  return option?.[props.optionLabel] ?? "";
}

function optionMatches(option, q) {
  if (!q) return true;
  const fields = [];

  if (props.searchProp) {
    if (Array.isArray(props.searchProp)) fields.push(...props.searchProp);
    else fields.push(props.searchProp);
  } else {
    // fallback: label only
    fields.push(props.optionLabel);
  }
  return fields.some((f) =>
    String(typeof f === "function" ? f(option) : option?.[f] ?? getLabel(option))
      .toLowerCase()
      .includes(q)
  );
}

function groupedFilter(options, q) {
  const result = [];

  let header = null;
  let parent = null;
  let children = [];

  const flush = () => {
    if (!header) {
      // Defensiver Reset: verhindert dass parent/children in die nächste Gruppe überlaufen
      parent = null;
      children = [];
      return;
    }

    const headerMatch = optionMatches(header, q);
    const parentMatch = parent && optionMatches(parent, q);

    const matchedChildren = children.filter(child =>
      optionMatches(child, q)
    );

    if (headerMatch || parentMatch) {
      result.push(header);

      if (parent) {
        result.push(parent);
      }

      result.push(...children);

    } else if (matchedChildren.length) {
      result.push(header);

      if (parent) {
        result.push(parent);
      }

      result.push(...matchedChildren);
    }

    header = null;
    parent = null;
    children = [];
  };


  for (const option of options) {

    if (option.__header) {
      flush();
      header = option;
    }
    else if (option.__parent) {
      parent = option;
    }
    else if (option.__child) {
      children.push(option);
    }
  }

  flush();

  return result;
}

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();

  if (props.groupedSearch && q) {
    return groupedFilter(normalizedOptions.value, q);
  }

  let list = normalizedOptions.value.filter(o => optionMatches(o, q));

  if (props.filterFn) {
    list = list.filter(o => props.filterFn(o, q));
  }

  return list;
});

function toggle() {
  if (props.disabled) return;
  if (open.value) {
    close();
  } else {
    open.value = true;
    emit("open");
    nextTick(() => {
      computePosition();
      focusSearch();
    });
  }
}

function close() {
  if (!open.value) return;
  open.value = false;
  highlightedIndex.value = -1;
  query.value = "";
  listMaxHeight.value = null;
  emit("close");
}

function focusSearch() {
  if (props.searchable) inputRef.value?.focus();
}

function selectOption(option) {
  if (!option) return;
  if (option.__header || option.disabled) return;
  const payload = props.returnObject ? option : option?.[props.optionKey];
  emit("update:modelValue", payload);
  emit("select", payload);
  if (props.closeOnSelect) close();
}

function onKeydown(e) {
  if (!open.value && ["ArrowDown", "Enter", " "].includes(e.key)) {
    e.preventDefault();
    toggle();
    return;
  }
  if (!open.value) return;
  if (e.key === "Escape") {
    e.preventDefault();
    close();
    return;
  }
  if (["ArrowDown", "ArrowUp"].includes(e.key)) {
    e.preventDefault();
    const len = filtered.value.length;
    if (!len) return;
    const step = e.key === "ArrowDown" ? 1 : -1;
    let next = highlightedIndex.value;
    // find next non-header option
    for (let i = 0; i < len; i++) {
      next = (next + step + len) % len;
      const opt = filtered.value[next];
      if (!opt?.__header) break;
    }
    highlightedIndex.value = next;
    scrollHighlightedIntoView();
  }
  if (e.key === "Enter") {
    e.preventDefault();
    if (highlightedIndex.value >= 0) selectOption(filtered.value[highlightedIndex.value]);
  }
}

function scrollHighlightedIntoView() {
  const container = listRef.value;
  if (!container) return;
  const items = container.querySelectorAll(".fs-item");
  if (highlightedIndex.value < 0 || highlightedIndex.value >= items.length) return;
  const el = items[highlightedIndex.value];
  const cTop = container.scrollTop;
  const cBottom = cTop + container.clientHeight;
  const eTop = el.offsetTop;
  const eBottom = eTop + el.offsetHeight;
  if (eBottom > cBottom) container.scrollTop = eBottom - container.clientHeight;
  else if (eTop < cTop) container.scrollTop = eTop;
}

function computePosition() {
  const el = triggerRef.value;
  if (!el) return;
  // detect theme on each open (in case parent context changed or component moved)
  if (el) {
    const themeHost = el.closest(".theme--light, .theme--dark");
    themeClass.value = themeHost
      ? [...themeHost.classList].find(
          (c) => c === "theme--light" || c === "theme--dark"
        ) || ""
      : "";
  }
  const rect = el.getBoundingClientRect();
  const viewH = window.innerHeight;
  const optionCount = filtered.value.length;
  const visibleCount = Math.min(optionCount, props.maxVisibleItems);
  // size-basiertes ItemHeight Mapping (visuelle Konsistenz vs Berechnung)
  const effItemHeight =
    props.size === "sm"
      ? Math.max(30, props.itemHeight - 8)
      : props.size === "lg"
      ? props.itemHeight + 6
      : props.itemHeight;
  // Natural list height only for available visible items
  const naturalListHeight = visibleCount * effItemHeight;
  // Chrome: top search area (if enabled) + internal padding/gap
  const chrome = (props.searchable ? 56 : 12) + 16; // refined: reduced base padding
  // Desired full popover height
  let desired = naturalListHeight + chrome;
  // If only 0 or 1 option -> shrink natural min height (no need large empty space)
  if (optionCount <= 1) {
    desired = optionCount * effItemHeight + (props.searchable ? 56 : 0) + 20;
  }
  // Preliminary estimate (not final clamp yet)
  let estHeight = Math.min(props.maxHeight, Math.max(props.minHeight, desired));
  const margin = props.popoverMargin;
  const spaceBelow = viewH - rect.bottom - margin;
  const spaceAbove = rect.top - margin;
  if (props.autoDirection) {
    direction.value = spaceBelow < estHeight && spaceAbove > spaceBelow ? "up" : "down";
  } else direction.value = "down";
  // Available vertical space for entire popover depending on direction
  const available = direction.value === "down" ? spaceBelow : spaceAbove;
  // Hard clamp to available (minus small breathing space)
  const finalHeight = Math.min(estHeight, available - 6);
  // Compute list scroll area height (finalHeight minus chrome). Ensure >= itemHeight when options > 0
  let maxList = finalHeight - chrome;
  if (optionCount === 0) maxList = 80; // empty state compact height
  if (optionCount > 0) {
    maxList = Math.max(
      Math.min(naturalListHeight, maxList),
      Math.min(effItemHeight + 4, maxList)
    );
  }
  listMaxHeight.value = Math.max(40, maxList);
  let top =
    direction.value === "down" ? rect.bottom + margin : rect.top - finalHeight - margin;
  // Clamp top so popover never leaves viewport vertically
  top = Math.max(margin, Math.min(top, viewH - finalHeight - margin));
  let left = rect.left;
  let width = Math.min(props.maxWidth, Math.max(props.minWidth, rect.width));
  if (props.matchTriggerWidth) {
    width = rect.width;
  } else if (props.autoWidth) {
    try {
      // Messe längste gefilterte Option (begrenze Messmenge für Performance)
      const sample = filtered.value.slice(
        0,
        Math.max(filtered.value.length, props.maxVisibleItems * 2)
      );
      let longest = "";
      for (const o of sample) {
        const lbl = getLabel(o) || "";
        if (lbl.length > longest.length) longest = lbl;
      }
      if (longest) {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const style = window.getComputedStyle(el);
        const font = `${style.fontWeight || 400} ${style.fontSize || "12px"} ${
          style.fontFamily || "sans-serif"
        }`;
        if (ctx) ctx.font = font;
        const metrics = ctx ? ctx.measureText(longest).width : longest.length * 7;
        const desired = metrics + 72; // Paddings + Chevron + optional Count
        width = Math.min(props.maxWidth, Math.max(props.minWidth, desired));
        measuredWidth.value = width;
      }
    } catch (e) {}
  }
  if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
  // Wenn Liste vollständig reinpasst -> kein Scroll nötig
  const totalListSpace = naturalListHeight;
  const fitsWithoutScroll = totalListSpace + chrome <= finalHeight;
  if (fitsWithoutScroll) {
    listMaxHeight.value = naturalListHeight; // exakte Höhe
  }
  // Use maxHeight; width dynamisch
  popoverStyle.value = {
    top: top + "px",
    left: left + "px",
    width: width + "px",
    maxHeight: finalHeight + "px",
  };
}

function handleReposition() {
  if (open.value) computePosition();
}

function handleClickOutside(e) {
  if (!open.value) return;
  const root = rootEl.value;
  if (root && !root.contains(e.target) && !e.target.closest(".fs-popover")) close();
}

const rootEl = ref(null);

watch(query, (val) => emit("search", val));

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("resize", handleReposition, { passive: true });
  window.addEventListener("scroll", handleReposition, { passive: true });
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("resize", handleReposition);
  window.removeEventListener("scroll", handleReposition);
});
</script>

<template>
  <div
    ref="rootEl"
    class="floating-select"
    :class="['size-' + props.size]"
    @keydown="onKeydown"
  >
    <div
      ref="triggerRef"
      @click="toggle"
      :class="['fs-trigger-wrapper', { 'is-open': open, 'is-disabled': disabled }]"
    >
      <slot name="trigger" :open="open" :toggle="toggle">
        <button
          type="button"
          :disabled="disabled"
          :aria-expanded="open.toString()"
          :aria-label="ariaLabel"
          class="fs-trigger-default"
        >
          <span class="fs-trigger-text">{{ ariaLabel }}</span>
          <svg
            v-if="showChevron"
            class="fs-chevron"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path fill="currentColor" d="M7 10l5 5 5-5z" />
          </svg>
        </button>
      </slot>
    </div>
    <teleport to="body">
      <transition name="fs-fade">
        <div
          v-if="open"
          class="fs-popover"
          :class="[
            themeClass,
            'dir-' + direction,
            'size-' + props.size,
            'fs-popover--stable',
          ]"
          :style="{ ...popoverStyle, zIndex: teleportZ }"
          role="dialog"
          :aria-label="ariaLabel"
        >
          <div v-if="searchable" class="fs-search-wrapper">
            <input
              ref="inputRef"
              v-model="query"
              type="text"
              class="fs-search"
              :placeholder="placeholder"
              autocomplete="off"
            />
          </div>
          <slot name="before-list" />
          <div
            ref="listRef"
            class="fs-list"
            :class="[{ 'wrap-mode': allowWrap, 'truncate-mode': !allowWrap }]"
            role="listbox"
            :style="listMaxHeight ? { maxHeight: listMaxHeight + 'px' } : null"
            :aria-activedescendant="
              highlightedIndex >= 0 && filtered[highlightedIndex]
                ? 'fs-item-' + filtered[highlightedIndex][optionKey]
                : undefined
            "
          >
            <div v-if="filtered.length === 0" class="fs-empty">
              <slot name="empty">Keine Treffer</slot>
            </div>
            <button
              v-for="(option, i) in filtered"
              :key="option[optionKey] ?? 'hdr-' + i"
              :id="'fs-item-' + (option[optionKey] ?? 'hdr-' + i)"
              type="button"
              class="fs-item"
              :class="{
                'is-highlighted': i === highlightedIndex,
                'is-header': !!option.__header,
                'is-child': !!option.__child,
                'is-parent': !!option.__parent,
              }"
              role="option"
              :aria-selected="i === highlightedIndex"
              :disabled="!!option.__header || !!option.disabled"
              @mouseenter="!option.__header && (highlightedIndex = i)"
              @mouseleave="highlightedIndex = -1"
              @click="selectOption(option)"
            >
              <slot
                name="item"
                :option="option"
                :index="i"
                :highlighted="i === highlightedIndex"
                :select="selectOption"
              >
                <span
                  class="fs-item-label"
                  :title="showFullOnHover && !allowWrap ? getLabel(option) : undefined"
                  >{{ getLabel(option) }}</span
                >
              </slot>
            </button>
          </div>
          <slot name="after-list" />
        </div>
      </transition>
    </teleport>
  </div>
</template>

<style scoped>
.floating-select {
  position: relative;
  display: inline-flex;
  width: 100%;
}
.filter-column .floating-select {
  width: 100%;
}
.fs-trigger-wrapper {
  cursor: pointer;
  width: 100%;
}
.filter-column .fs-trigger-wrapper,
.filter-column .fs-trigger-wrapper > * {
  width: 100%;
}
.fs-trigger-wrapper.is-disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
.fs-trigger-default {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font: inherit;
  font-size: 0.66rem;
  padding: 0.55rem 0.7rem;
  background: var(--surface-low, rgba(255, 255, 255, 0.04));
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  color: #d8dde3;
  cursor: pointer;
  transition: 0.16s;
  width: 100%;
  justify-content: space-between;
}
.theme--light .fs-trigger-default,
.fs-popover.theme--light .fs-trigger-default {
  background: #f1f5f9;
  border-color: #d5dee7;
  color: #1e2c38;
}
.fs-trigger-default:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.25);
}
.theme--light .fs-trigger-default:hover,
.fs-popover.theme--light .fs-trigger-default:hover {
  background: #e9eef3;
  border-color: #c8d2dd;
}
.fs-trigger-default:focus {
  outline: none;
  box-shadow: 0 0 0 2px
    color-mix(in srgb, var(--sellwerk-color-secondary, #28a) 55%, transparent);
}
.fs-chevron {
  transition: 0.22s;
}
.is-open .fs-chevron {
  transform: rotate(180deg);
}

.fs-popover {
  position: fixed;
  top: 0;
  left: 0;
  background: var(--surface-elevated, rgba(20, 23, 28, 0.96));
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
  padding: 0.55rem 0.6rem 0.65rem;
  box-shadow: 0 20px 46px -22px rgba(0, 0, 0, 0.55), 0 10px 28px -14px rgba(0, 0, 0, 0.4);
  width: 320px;
  backdrop-filter: blur(9px) saturate(150%);
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  animation: fsPop 0.16s ease;
}
.fs-popover.fs-popover--stable {
  min-height: 220px;
}
.theme--light .fs-popover,
.fs-popover.theme--light {
  background: rgba(255, 255, 255, 0.95);
  border-color: #d8e2ec;
  box-shadow: 0 18px 44px -20px rgba(30, 41, 59, 0.25),
    0 8px 22px -12px rgba(30, 41, 59, 0.18);
}
.fs-popover.dir-up {
  transform-origin: bottom center;
}
.fs-popover.dir-down {
  transform-origin: top center;
}

.fs-search-wrapper {
  padding: 0.15rem 0.2rem 0;
}
.fs-search {
  width: 100%;
  font: inherit;
  font-size: 0.7rem;
  padding: 0.5rem 0.6rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  outline: none;
  transition: 0.16s;
}
.fs-search:focus {
  border-color: var(--sellwerk-color-secondary);
  background: rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 0 2px
    color-mix(in srgb, var(--sellwerk-color-secondary) 55%, transparent);
}
.theme--light .fs-search,
.fs-popover.theme--light .fs-search {
  background: #f1f5f9;
  border-color: #d5dee7;
  color: #0f212d;
}
.theme--light .fs-search:focus,
.fs-popover.theme--light .fs-search:focus {
  border-color: var(--sellwerk-color-main);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--sellwerk-color-main) 55%, transparent);
  background: #fff;
}

.fs-list {
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.15rem 0.15rem 0.05rem;
  scrollbar-width: thin;
  flex: 1 1 auto;
  min-height: 0;
}
.fs-item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  text-align: left;
  padding: 0.45rem 0.55rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(255, 255, 255, 0.04);
  border-radius: 14px;
  cursor: pointer;
  transition: 0.16s;
  font: inherit;
  font-size: 0.66rem;
  line-height: 1.15;
  color: #d8dde3;
}
.fs-item:hover,
.fs-item.is-highlighted {
  background: rgba(255, 255, 255, 0.07);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.25);
}
.fs-list button:has(.gd-heading) {
  padding: 0 !important;
  border: 0 !important;
  background-color: transparent !important;
  background: transparent !important;
  cursor: default;
  border-radius: 0 !important;
  box-shadow: none !important;
}
.theme--light .fs-item,
.fs-popover.theme--light .fs-item {
  background: #f8fafc;
  border-color: #dfe5eb;
  color: #1e2c38;
}
.theme--light .fs-item:hover,
.theme--light .fs-item.is-highlighted,
.fs-popover.theme--light .fs-item:hover,
.fs-popover.theme--light .fs-item.is-highlighted {
  background: #eef2f6;
  color: #111e28;
  border-color: #c9d4de;
}
/* is-header overrides are handled via !important on the base rule above */
/* Indent entire option tile for child items */
.fs-item.is-child,
.fs-item.is-parent {
  margin-left: 0.9rem;
}
/* Focus ring for accessibility */
.fs-item:focus-visible {
  outline: 2px solid var(--sellwerk-color-secondary);
  outline-offset: 2px;
}
.theme--light .fs-item:focus-visible,
.fs-popover.theme--light .fs-item:focus-visible {
  outline: 2px solid var(--sellwerk-color-main);
}
.fs-empty {
  text-align: center;
  padding: 1.1rem 0.75rem;
  font-size: 0.64rem;
  opacity: 0.6;
}
/* No wrap + truncation for long labels */
/* Truncation (default) */
.truncate-mode .fs-item,
.truncate-mode .fs-item .fs-item-label,
.truncate-mode .fs-item :deep(.label),
.truncate-mode .fs-item :deep(.cur-label) {
  white-space: nowrap;
}
.truncate-mode .fs-item .fs-item-label,
.truncate-mode .fs-item :deep(.label),
.truncate-mode .fs-item :deep(.cur-label) {
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 1 auto;
}
/* Wrap mode (optional) */
.wrap-mode .fs-item {
  align-items: flex-start;
}
.wrap-mode .fs-item .fs-item-label {
  white-space: normal;
  word-break: break-word;
  line-height: 1.25;
}
/* Slightly tighter vertical space when many items */
.fs-list.compact .fs-item {
  padding: 0.38rem 0.55rem 0.42rem;
}

.fs-fade-enter-active,
.fs-fade-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}
.fs-fade-enter-from,
.fs-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
@keyframes fsPop {
  from {
    opacity: 0;
    transform: scale(0.97);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Size variants */
.fs-popover.size-sm {
  padding: 0.45rem 0.5rem 0.55rem;
  border-radius: 16px;
}
.fs-popover.size-sm .fs-search {
  font-size: 0.62rem;
  padding: 0.4rem 0.5rem;
  border-radius: 12px;
}
.fs-popover.size-sm .fs-item {
  padding: 0.35rem 0.45rem 0.4rem;
  font-size: 0.6rem;
  border-radius: 12px;
}
.fs-popover.size-sm .fs-item.is-header {
  padding: 0;
  border: 0;
  background-color: transparent;
  background: transparent;
  border-radius: 0;
}
.fs-popover.size-sm .fs-empty {
  font-size: 0.6rem;
  padding: 0.85rem 0.6rem;
}
.filter-column .floating-select.size-sm .fs-trigger-default,
.filter-column .floating-select .fs-trigger-default.size-sm {
  font-size: 0.6rem;
  padding: 0.42rem 0.55rem;
  border-radius: 12px;
}

.fs-popover.size-lg {
  padding: 0.7rem 0.75rem 0.85rem;
}
.fs-popover.size-lg .fs-search {
  font-size: 0.72rem;
  padding: 0.55rem 0.65rem;
}
.fs-popover.size-lg .fs-item {
  padding: 0.55rem 0.65rem 0.6rem;
  font-size: 0.7rem;
}
.fs-popover.size-lg .fs-empty {
  font-size: 0.68rem;
}

/* Root trigger adjustments (outside popover) */
.floating-select.size-sm .fs-trigger-default {
  font-size: 0.6rem;
  padding: 0.4rem 0.55rem;
  border-radius: 12px;
}
.floating-select.size-lg .fs-trigger-default {
  font-size: 0.72rem;
  padding: 0.65rem 0.85rem;
}
</style>
